import React from 'react';
import {
  Box, makeStyles, Typography, Divider,
} from '@material-ui/core';
import Button from 'Components/UI/Button';
import modalConsts from 'Consts/ModalNames';
import { addQuery } from 'Helpers/Query';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useMobileLayout from 'Hooks/useMobileLayout';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';

import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import ListItem from './ListItem';
import ListItemContainer from './ListItem/ListItemContainer';
import ListItemContent from './ListItem/ListItemContent';
import ListItemDescription from './ListItem/ListItemDescription';
import ListItemLabel from './ListItem/ListItemLabel';
import FileChips from './ListItem/FileChips';
import ListItemActions from './ListItem/ListItemActions';
import sortActionItemsByCompletedThenPriority from './helpers';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  noTodoPaper: {
    width: '100%',
    display: 'flex',
    padding: theme.spacing(2),
    borderRadius: theme.radius.md,
  },
  seeAll: {
    maxWidth: theme.dimensions.sm,
    alignSelf: 'center',
    marginTop: theme.spacing(1),
  },
  wrapper: {
    flexGrow: 1,
    flexBasis: '35%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  columns: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  rows: {
    display: 'flex',
    width: '100%',
    flexFlow: 'column',
  },
  fileChip: {
    maxWidth: theme.dimensions.xs,
    backgroundColor: theme.palette.facet.accentGreen,
    color: theme.palette.common.white,
    '&:focus': {
      backgroundColor: theme.palette.facet.accentGreen,
      color: theme.palette.common.white,
    },
    marginRight: theme.spacing(1),
  },
  label: {
    width: '25%',
  },
  content: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
  },
  alignStart: {
    alignSelf: 'flex-start',
    alignItems: 'center',
  },
  item: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  alternativeForm: {
    flexBasis: '100%',
  },
  [theme.breakpoints.down('md')]: {
    item: {
      flexDirection: 'column',
    },
    label: {
      width: '100%',
    },
    content: {
      flexBasis: '100%',
      width: '100%',
      flexDirection: 'column',
    },
    iconButton: {
      justifySelf: 'center',
    },
    wrapper: {
      flexDirection: 'column-reverse',
      paddingTop: theme.spacing(1),
      flexBasis: '100%',
      width: '100%',
    },
    actions: {
      paddingBottom: theme.spacing(1),
      width: '100%',
      flexDirection: 'row',
    },
  },
}));

export default function ActionItemList(props) {
  const {
    actionItemCollection,
    replaceActionItem,
    refreshActionItems,
    showDivider,
    readOnly,
    useGoalModal,
    renderLimit: renderLimitProp,
  } = props;
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const sortedActionItems = sortActionItemsByCompletedThenPriority(
    actionItemCollection
      .resources()
      .list(),
  );
  const renderLimit = renderLimitProp || sortedActionItems.length;

  const ActionItemListItems = sortActionItemsByCompletedThenPriority(
    actionItemCollection
      .resources()
      .list(),
  ).slice(0, renderLimit).map(r => (
    <Paper variant="tight" className={classes.item}>
      <ListItemContainer resource={r} key={r.id()} refreshActionItems={refreshActionItems} replaceActionItem={replaceActionItem}>
        {(actionItemListItemProps) => {
          const {
            expand,
            fileExpand,
            fileLinkCollection,
            alternativeToggleDisplay,
            setExpand,
            markAsComplete,
          } = actionItemListItemProps;
          let AlternantiveForm = null;
          if (alternativeToggleDisplay) {
            const valid = false;
            const fileCollection = null;
            const formResource = null;
            const formLayout = null;
            const submitLabelOverride = '';
            const displaySuccessLabel = '';

            AlternantiveForm = valid && (
              <MetaFormInline
                className={classes.alternativeForm}
                classes={{
                  root: classes.alternativeForm,
                  fieldListControlNotFirst: classes.alternativeFormFieldListControl,
                }}
                resource={formResource}
                onSubmit={() => {
                  return fileCollection.refresh().then(() => {
                    displaySuccess(displaySuccessLabel);
                    markAsComplete();
                    return refreshActionItems();
                  });
                }}
                onFailure={err => displayErrorMessage(err, err.errors)}
                fullWidth
                layout={formLayout}
                submitLabelOverride={submitLabelOverride}
              />
            );
          }

          return (
            <ListItem className={classes.item} expand={expand} readOnly={readOnly} useGoalModal={useGoalModal}>
              <ListItemContent className={classNames(classes.content, {
                [classes.columns]: fileExpand,
                [classes.wrap]: fileExpand,
              })}
              >
                <ListItemLabel
                  resource={r}
                  expand={expand}
                  fileExpand={fileExpand}
                  setExpand={setExpand}
                  className={classNames(classes.label, { [classes.alignStart]: expand || fileExpand })}
                />
                <div className={classNames(classes.wrapper, { [classes.alignStart]: expand || fileExpand })}>
                  <div
                    className={classNames({
                      [classes.columns]: !isMobile,
                      [classes.rows]: isMobile,
                    })}
                  >
                    {fileLinkCollection && (
                      <div className={classes.fileChips}>
                        <FileChips
                          classOverride={classes.fileChip}
                          fileCollection={fileLinkCollection}
                          {...actionItemListItemProps}
                        />
                      </div>
                    )}
                    <ListItemDescription
                      expand={expand}
                      description={r.attr('description')}
                      {...actionItemListItemProps}
                    />
                  </div>
                  {!readOnly && (
                    <ListItemActions
                      className={classNames(classes.actions, classes.alignStart)}
                      resource={r}
                      {...actionItemListItemProps}
                    />
                  )}
                </div>
                {AlternantiveForm}
              </ListItemContent>
            </ListItem>
          );
        }}
      </ListItemContainer>
    </Paper>
  ));

  return (
    <Box className={classes.root}>
      {ActionItemListItems.length ? (
        <>
          {showDivider ? ActionItemListItems.map(Item => (
            <>
              {Item}
              <Divider flexItem style={{ height: 1 }} />
            </>
          )) : ActionItemListItems}
          {actionItemCollection.resources().length() > renderLimit && (
            <Button
              onClick={() => addQuery({ [modalConsts.modal]: modalConsts.actionItemList })
              }
              className={classes.seeAll}
              color="primary"
              variant="contained"
              size="large"
            >
              See All
            </Button>
          )}
        </>
      ) : (
        <Paper className={classes.noTodoPaper} variant="tight">
          <Typography variant="body1" align="center">
            {"Use this space to keep a list of your to-do's"}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

ActionItemList.defaultProps = {
  renderLimit: null,
  useGoalModal: false,
  readOnly: false,
  showDivider: false,
};

ActionItemList.propTypes = {
  renderLimit: PropTypes.number,
  actionItemCollection: PropTypes.object.isRequired,
  replaceActionItem: PropTypes.func.isRequired,
  refreshActionItems: PropTypes.func.isRequired,
  showDivider: PropTypes.bool,
  readOnly: PropTypes.bool,
  useGoalModal: PropTypes.bool,
};
